import type { TemplateCalculatedItem, TemplateMeasurementContext } from "./template-quote-calculations";

/**
 * UK template scope references only. All prices still come from the effective
 * Cost Library. Existing saved items and other markets never enter this helper.
 */
export function prepareBathroomTemplateScope<T extends TemplateCalculatedItem>(
  seeds: T[], ctx: TemplateMeasurementContext,
): T[] {
  if (ctx.market && ctx.market !== "uk") return seeds;
  const make = (seed: T, name: string, target: string, unit: string, quantity = 1, description = ""): T => ({
    ...seed, id: `${seed.id}-${target.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    name, description, unit, quantity, libraryNames: [target], costLibraryTaskId: undefined,
    quoteCostRecipe: null, unitCost: 0, labourCost: 0, materialCost: 0, plantCost: 0,
  });
  // Common additions must not recreate the retired room packages either.
  if (seeds.some(i => ["En-suite Bathroom", "WC / Cloakroom Pack"].includes(i.name))) {
    const expanded = seeds.flatMap(seed => {
      if (!["En-suite Bathroom", "WC / Cloakroom Pack"].includes(seed.name)) return [seed];
      const shower = seed.name === "En-suite Bathroom";
      const count = seed.quantity;
      return [
        make(seed, "WC Installation", "Install WC (close coupled)", "each", count),
        make(seed, "Basin and Taps Installation", "Install basin and taps", "each", count),
        make(seed, "Bathroom Extractor Fan", "Install extractor fan", "each", count,
          "Supply and install one bathroom extractor fan. Excludes lighting, sockets and general room wiring covered by the main electrical scope."),
        ...(shower ? [
          make(seed, "Shower Enclosure Installation", "Install shower enclosure", "each", count),
          make(seed, "Shower Mixer Installation", "Install shower mixer and rail", "each", count),
        ] : []),
        make(seed, "First-Fix Plumbing", "First fix plumbing (per point)", "each", (shower ? 3 : 2) * count),
        make(seed, "Waste / Soil Distribution", "Waste / Soil Distribution", "item", count,
          "Distribution drainage only; excludes fixture traps, wastes and final connections included with the fixtures. Confirm required alterations."),
        ...[
          ["Bathroom Wall Tiling", "Wall tiling (standard ceramic)", shower ? 10 : 3],
          ["Bathroom Floor Tiling", "Floor tiling (porcelain)", shower ? 4.5 : 2],
          ...(shower ? [["Wet-Area Tanking", "Waterproofing / tanking to wet areas", 4.5]] : []),
        ].map(([name, target, area]) => ({
          ...make(seed, String(name), String(target), "sqm", Number(area) * count),
          measurementWarning: `Provisional reference: ${area}m² per room; confirm actual area and avoid work already included elsewhere.`,
        })),
      ];
    });
    return prepareBathroomTemplateScope(expanded, ctx);
  }
  if (ctx.projectType === "full_house_renovation") {
    const bathrooms = seeds.filter(i => i.name === "Bathroom Renovation").reduce((n, i) => n + i.quantity, 0);
    return seeds.flatMap(seed => {
      if (bathrooms && ["Flooring Throughout", "Full Redecoration"].includes(seed.name)) {
        const area = (seed.name === "Flooring Throughout" ? 7 : 15.4) * bathrooms;
        return [{ ...seed,
          description: `${seed.description} Excludes bathroom ${seed.name === "Flooring Throughout" ? "floors" : "tiled wall surfaces"} priced separately.`,
          quantityAreaExclusion: { area, basis: "Provisional bathroom tiled area priced separately; confirm room measurements." },
        }];
      }
      if (["Full Replumb", "Plumbing Works"].includes(seed.name)) return [{
        ...seed,
        description: `${seed.description} Distribution/first-fix only in rooms with separately quoted fixtures: exclude their final connections and installation labour.`,
      }];
      if (seed.name !== "Bathroom Renovation") return [seed];
      const text = seed.description ?? "";
      const allSupplied = /(?:client|customer).{0,20}(?:supply|supplied).{0,15}(?:all fixtures|all fittings|everything)/i.test(text);
      const supplied = allSupplied || /client to supply suite|customer.supplied suite|reuse.{0,15}suite/i.test(text);
      const suiteSuffix = supplied ? " Fitting Only" : " Installation";
      const brassSuffix = allSupplied || /(?:client|customer).{0,20}(?:supply|supplied).{0,15}(?:brassware|taps|mixers)/i.test(text) ? " Fitting Only" : " Installation";
      const screenSuffix = allSupplied || /(?:client|customer).{0,20}(?:supply|supplied).{0,15}(?:screen|enclosure)/i.test(text) ? " Fitting Only" : " Installation";
      const result = [
        make(seed, `Bathroom Suite${suiteSuffix}`, `Bathroom Suite${suiteSuffix}`, "item", seed.quantity),
        make(seed, `Brassware / Mixers${brassSuffix}`, `Brassware / Mixers${brassSuffix}`, "item", seed.quantity),
        make(seed, `Shower Screen${screenSuffix}`, `Shower Screen${screenSuffix}`, "each", seed.quantity),
      ];
      // The replaced package explicitly included tiling. Keep it independently
      // visible, with a stated provisional area rather than silently losing it.
      for (const [name, target, area] of [
        ["Bathroom Wall Tiling", "Wall tiling (standard ceramic)", 15.4],
        ["Bathroom Floor Tiling", "Floor tiling (porcelain)", 7],
      ] as const) result.push({
        ...make(seed, name, target, "sqm", area * seed.quantity),
        measurementWarning: `Provisional reference: ${area}m² per bathroom; confirm the actual tiled area.`,
      });
      return result;
    });
  }
  if (ctx.projectType !== "bathroom_refurb") return seeds;
  const shower = seeds.some(i => i.name === "Shower (Labour Only)");
  const bath = seeds.some(i => i.name === "Bath (Labour Only)");
  const fixtureCount = 2 + Number(shower) + Number(bath);
  return seeds.flatMap(seed => {
    if (seed.name === "Sanitaryware (Labour Only)") {
      if (shower && bath) return [
        make(seed, "Bathroom Suite Installation", "Bathroom Suite Installation", "item"),
        make(seed, "Brassware / Mixers Installation", "Brassware / Mixers Installation", "item"),
        make(seed, "Shower Screen Installation", "Shower Screen Installation", "each"),
      ];
      // Smaller sets use complete individual fixture entries. Their included
      // taps/wastes/glass are NOT repeated as a full brassware or screen set.
      return [
        make(seed, "WC Installation", "Install WC (close coupled)", "each"),
        make(seed, "Basin and Taps Installation", "Install basin and taps", "each"),
        ...(bath ? [make(seed, "Bath and Taps Installation", "Install bath and taps", "each")] : []),
        ...(shower ? [
          make(seed, "Shower Enclosure Installation", "Install shower enclosure", "each"),
          make(seed, "Shower Mixer Installation", "Install shower mixer and rail", "each"),
        ] : []),
      ];
    }
    if (["Shower (Labour Only)", "Bath (Labour Only)"].includes(seed.name)) return [];
    if (seed.name === "Plumbing Works") return [
      make(seed, "First-Fix Plumbing", "First fix plumbing (per point)", "each", fixtureCount,
        "Hot/cold distribution pipework to prepared fixture points only; excludes fixture fixing and final local fixture connections."),
      { ...make(seed, "Waste / Soil Distribution", "Waste / Soil Distribution", "item"),
        description: "Waste/soil distribution runs to prepared fixture outlets only. Excludes local traps, fixture wastes, WC pan connector and final connections already in fixture items.",
        measurementWarning: "Confirm required waste/soil alterations and runs; do not repeat existing adequate pipework." },
    ];
    if (seed.name === "Bathroom Accessories") return [
      make(seed, "Bathroom Accessories Installation", "Bathroom Accessories Installation", "item"),
      ...(/heated\s+towel\s+rail/i.test(seed.description ?? "") ? [{
        ...make(seed, "Heated Towel Rail", "Install heated towel rail", "each", seed.quantity,
          "Supply and install heated towel rail. Reference assumes connection to existing heating pipework; confirm whether a plumbed or electric rail is required. Excludes new distribution pipework and general electrical work."),
        category: "plumbing", trade: "second_fix_plumbing", section: "SERVICES",
        measurementWarning: "Connection type assumed: existing heating pipework. Confirm plumbed versus electric rail before sending.",
      }] : []),
    ];
    return [seed];
  });
}
